import React, { useState, useRef, useEffect } from 'react';
import { IoSparkles, IoClose } from 'react-icons/io5';
import { IoIosArrowUp } from 'react-icons/io';
import { HiOutlineSparkles } from 'react-icons/hi2';
import { ReactComponent as ChatGpt4o } from '../../../assets/chatgpt/AI Models (1).svg';
import { ReactComponent as ChatGpt4 } from '../../../assets/chatgpt/type=Chat gpt 4.svg';
import { ReactComponent as Claude3Sonnet } from '../../../assets/chatgpt/type=Claude 3 Sonnet.svg';
import { ReactComponent as Claude3Opus } from '../../../assets/chatgpt/type=Claude 3 Opus.svg';
import { ReactComponent as Gemini15Flash } from '../../../assets/chatgpt/type=Geminni 1.5 Flash.svg';
import { ReactComponent as Gemini15Pro } from '../../../assets/chatgpt/type=Geminni 1.5 Pro.svg';
import { useSelector } from 'react-redux';
import Styles from './FloatingAIBar.module.scss';

const modelOptions = [
  // OpenAI GPT Models
  { value: 'gpt-5.5', label: 'GPT-5.5', icon: ChatGpt4o, description: 'Most capable GPT model', premium: true },
  { value: 'gpt-5.4', label: 'GPT-5.4', icon: ChatGpt4o, description: 'Fast & capable GPT model', premium: true },
  { value: 'gpt-5.1', label: 'GPT-5.1', icon: ChatGpt4o, description: 'Best for coding & agentic tasks' },
  { value: 'gpt-5-pro', label: 'GPT-5 Pro', icon: ChatGpt4, description: 'Smarter, more precise responses', premium: true },
  { value: 'gpt-5-mini', label: 'GPT-5 Mini', icon: ChatGpt4o, description: 'Fast & cost-efficient' },
  { value: 'gpt-5-nano', label: 'GPT-5 Nano', icon: ChatGpt4o, description: 'Fastest & most cost-efficient' },
  // Anthropic Claude Models
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5', icon: Claude3Sonnet, description: "Anthropic's latest & smartest", premium: true },
  { value: 'claude-opus-4-7', label: 'Claude Opus 4.7', icon: Claude3Opus, description: "Anthropic's most capable", premium: true },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', icon: Claude3Sonnet, description: "Anthropic's balanced model", premium: true },
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6', icon: Claude3Opus, description: "Previous gen most capable", premium: true },
  // Google Gemini Models
  { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', icon: Gemini15Flash, description: "Google's latest & fastest", premium: true },
  { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro', icon: Gemini15Pro, description: "Google's most intelligent", premium: true },
];

function FloatingAIBar({ handlePromptAI, input, setInput, disabled, showEditor }) {
  const [selectedModel, setSelectedModel] = useState('gpt-5.1');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const inputRef = useRef(null);
  const modelPickerRef = useRef(null);
  const barRef = useRef(null);

  const { user } = useSelector((state) => state.auth);
  const { subscriptions } = user || {};
  const { current_subscription: currentSubscription } = subscriptions ?? {};
  const isBasicPlan = currentSubscription?.plan_name === 'Basic Plan';

  const selectedModelData = modelOptions.find(m => m.value === selectedModel) || modelOptions[0];
  const IconComponent = selectedModelData.icon;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelPickerRef.current && !modelPickerRef.current.contains(event.target)) {
        setShowModelPicker(false);
      }
      if (barRef.current && !barRef.current.contains(event.target) && !input.trim()) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [input]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const handleSubmit = () => {
    if (input.trim()) {
      const promptText = input;
      // Keep input visible so user can see what they sent
      showEditor(); // Open the editor sidebar first
      handlePromptAI(promptText, selectedModel); // Then trigger AI generation
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && input.trim()) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      setIsExpanded(false);
      inputRef.current?.blur();
    }
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    setShowModelPicker(false);
    setInput(''); // Clear prompt on collapse
    inputRef.current?.blur();
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  const handleModelSelect = (model) => {
    if (model.premium && isBasicPlan) return;
    setSelectedModel(model.value);
    setShowModelPicker(false);
  };

  if (disabled) {
    return null;
  }

  return (
    <div className={Styles.floating_container}>
      <div
        ref={barRef}
        className={`${Styles.floating_bar} ${isExpanded ? Styles.expanded : ''}`}
      >
        {/* Top Row: AI Icon + Textarea */}
        <div className={Styles.input_row}>
          {/* AI Icon */}
          <div className={Styles.ai_icon}>
            <HiOutlineSparkles />
          </div>

          {/* Input Field */}
          <textarea
            ref={inputRef}
            placeholder="Ask AI to edit or generate..."
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            className={Styles.input_field}
            rows={1}
          />

          {/* Send Button - Shows inline when collapsed */}
          {!isExpanded && (
            <button
              onClick={handleSubmit}
              disabled={!input.trim()}
              className={Styles.send_button}
              type="button"
            >
              <IoSparkles />
            </button>
          )}
        </div>

        {/* Bottom Row: Actions + Model Selector + Send - Shows when expanded */}
        {isExpanded && (
          <div className={Styles.bottom_row}>
            {/* Left side: Esc hint */}
            <button
              className={Styles.esc_hint}
              onClick={handleCollapse}
              type="button"
              aria-label="Press Escape to close"
            >
              <kbd>Esc</kbd>
              <span>to close</span>
            </button>

            {/* Right side: Model Selector + Send */}
            <div className={Styles.right_controls}>
              <div className={Styles.model_selector} ref={modelPickerRef}>
                <button
                  className={Styles.model_button}
                  onClick={() => setShowModelPicker(!showModelPicker)}
                  type="button"
                >
                  <span className={Styles.model_icon}>
                    <IconComponent />
                  </span>
                  <span className={Styles.model_label}>{selectedModelData.label}</span>
                  <IoIosArrowUp className={`${Styles.arrow} ${showModelPicker ? Styles.arrow_open : ''}`} />
                </button>

                {/* Model Dropdown */}
                {showModelPicker && (
                  <div className={Styles.model_dropdown}>
                    <div className={Styles.dropdown_header}>
                      <span>Select Model</span>
                    </div>
                    <div className={Styles.model_list}>
                      {modelOptions.map((model) => {
                        const ModelIcon = model.icon;
                        const isDisabled = model.premium && isBasicPlan;
                        const isSelected = model.value === selectedModel;

                        return (
                          <button
                            key={model.value}
                            className={`${Styles.model_option} ${isSelected ? Styles.selected : ''} ${isDisabled ? Styles.disabled : ''}`}
                            onClick={() => handleModelSelect(model)}
                            disabled={isDisabled}
                            type="button"
                          >
                            <span className={Styles.model_option_icon}>
                              <ModelIcon />
                            </span>
                            <div className={Styles.model_info}>
                              <span className={Styles.model_name}>{model.label}</span>
                              <span className={Styles.model_desc}>{model.description}</span>
                            </div>
                            {isSelected && (
                              <svg className={Styles.check_icon} viewBox="0 0 16 16" fill="none">
                                <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                            {isDisabled && (
                              <span className={Styles.premium_badge}>Pro</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Send Button */}
              <button
                onClick={handleSubmit}
                disabled={!input.trim()}
                className={Styles.send_button}
                type="button"
              >
                <IoSparkles />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Keyboard hint */}
      {isExpanded && input.trim() && (
        <div className={Styles.keyboard_hint}>
          Press <kbd>Enter</kbd> to generate
        </div>
      )}
    </div>
  );
}

export default FloatingAIBar;
